/**
 * Loads Cloudflare Turnstile at the first sign of upload intent.
 *
 * Turnstile only guards the upload and the email forms, but its api.js pulls
 * in a challenge runtime on every page load, including visits that never touch
 * the dropzone. Deferring it to the first pointer or keyboard contact with the
 * dropzone takes it off the initial main thread entirely, and by the time a
 * user has picked a file it has long since arrived.
 *
 * No CSP change is needed: challenges.cloudflare.com is already allowed in
 * script-src, and this module adds no inline script of its own.
 */

/* Copy this verbatim from the tag being removed from index.html, including the
   onload parameter, so whatever callback the inline bridge defines still fires
   with the same name. */
const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileReady&render=explicit';

/** How long to keep checking for the global after the script reports loaded. */
const READY_TIMEOUT_MS = 5000;
const READY_POLL_MS = 50;

let pending = null;

function present() {
  return typeof window !== 'undefined' && typeof window.turnstile !== 'undefined';
}

/**
 * api.js normally publishes window.turnstile before its load event, but the
 * explicit render mode has been known to publish it a tick later. A short
 * bounded wait is cheaper than a race that only shows up on slow devices.
 */
function whenReady() {
  return new Promise((resolve) => {
    if (present()) {
      resolve(true);
      return;
    }

    const started = Date.now();
    const timer = setInterval(() => {
      if (present()) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - started > READY_TIMEOUT_MS) {
        clearInterval(timer);
        resolve(false);
      }
    }, READY_POLL_MS);
  });
}

/**
 * Resolves true when window.turnstile is usable, false if it could not be
 * loaded. Await this before any turnstile.render() call.
 */
export function ensureTurnstile() {
  if (present()) return Promise.resolve(true);
  if (pending) return pending;

  pending = new Promise((resolve, reject) => {
    const el = document.createElement('script');

    el.src = SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('Could not load Turnstile'));
    document.head.appendChild(el);
  })
    .then(whenReady)
    .catch((err) => {
      pending = null;
      console.error('Turnstile failed to load', err);
      return false;
    });

  return pending;
}

/**
 * Watches an element for the first sign that an upload is coming and starts
 * the load then.
 *
 * Pass onFirstContact to do more than load, typically to render a widget once
 * the script has arrived. With no callback it just calls ensureTurnstile.
 *
 * Focus alone is too narrow: most people drag a file straight onto the
 * dropzone without ever focusing it, and the drag is the earliest reliable
 * signal there is. Every listener is once only, so the cost after the first
 * contact is nothing.
 */
export function armTurnstile(el, onFirstContact) {
  if (!el) return;

  const start = () => {
    if (typeof onFirstContact === 'function') onFirstContact();
    else ensureTurnstile();
  };

  const events = ['pointerdown', 'focusin', 'dragenter', 'dragover', 'change'];

  events.forEach((name) => {
    el.addEventListener(name, start, { once: true, passive: true });
  });
}
